import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface ConfigState {
	selectedCourses: string[];
	selectedCourse: string;
	selectedStream: string;
	selectedStreamId: number | null;
	numberOfCourses: number;
	showCoursesMultiSelect: boolean;
	// Multi-course flow state
	currentCourseIndex: number;
	completedCourses: string[];
	totalCoursesToAdd: number;
	// Track added courses to prevent re-selection
	addedCourses: string[];
	// Course counts for technical and vocational
	technicalCoursesCount: number;
	vocationalCoursesCount: number;
}

const initialState: ConfigState = {
	selectedCourses: [],
	selectedCourse: "",
	selectedStream: "",
	selectedStreamId: null,
	numberOfCourses: 1,
	showCoursesMultiSelect: false,
	// Multi-course flow state
	currentCourseIndex: 0,
	completedCourses: [],
	totalCoursesToAdd: 0,
	// Track added courses to prevent re-selection
	addedCourses: [],
	// Course counts for technical and vocational
	technicalCoursesCount: 0,
	vocationalCoursesCount: 0,
};

const courseSlice = createSlice({
	name: "course",
	initialState,
	reducers: {
		setSelectedCourses(state, action: PayloadAction<string[] | string>) {
			if (Array.isArray(action.payload)) {
				// If payload is array, replace the entire array
				state.selectedCourses = action.payload;
			} else {
				// If payload is string, push to array (avoiding duplicates)
				if (!state.selectedCourses.includes(action.payload)) {
					state.selectedCourses.push(action.payload);
				} else {
					// If course already exists, move it to the end (most recent)
					state.selectedCourses = state.selectedCourses.filter(course => course !== action.payload);
					state.selectedCourses.push(action.payload);
				}
			}
		},
		setSelectedCourse(state, action: PayloadAction<string>) {
			state.selectedCourse = action.payload;
		},
		setSelectedStream(state, action: PayloadAction<string>) {
			state.selectedStream = action.payload;
		},
		setSelectedStreamId(state, action: PayloadAction<number | null>) {
			state.selectedStreamId = action.payload;
		},
		setNumberOfCourses(state, action: PayloadAction<number>) {
			state.numberOfCourses = action.payload;
		},
		// Multi-course flow actions
		setCurrentCourseIndex(state, action: PayloadAction<number>) {
			state.currentCourseIndex = action.payload;
		},
		addCompletedCourse(state, action: PayloadAction<string>) {
			if (!state.completedCourses.includes(action.payload)) {
				state.completedCourses.push(action.payload);
			}
		},
		setTotalCoursesToAdd(state, action: PayloadAction<number>) {
			state.totalCoursesToAdd = action.payload;
		},
		resetMultiCourseFlow(state) {
			state.currentCourseIndex = 0;
			state.completedCourses = [];
			state.totalCoursesToAdd = 0;
		},

		clearSelectedCourses(state) {
			state.selectedCourses = [];
		},
		addCourseToAddedList(state, action: PayloadAction<string>) {
			if (!state.addedCourses.includes(action.payload)) {
				state.addedCourses.push(action.payload);
			}
		},
		removeCourseFromAddedList(state, action: PayloadAction<string>) {
			state.addedCourses = state.addedCourses.filter(courseId => courseId !== action.payload);
		},
		clearAddedCourses(state) {
			state.addedCourses = [];
		},
		setTechnicalCoursesCount(state, action: PayloadAction<number>) {
			state.technicalCoursesCount = action.payload;
		},
		setVocationalCoursesCount(state, action: PayloadAction<number>) {
			state.vocationalCoursesCount = action.payload;
		},
	},
});

export const {
	setSelectedCourses,
	clearSelectedCourses,
	setSelectedCourse,
	setSelectedStream,
	setSelectedStreamId,
	setNumberOfCourses,
	setCurrentCourseIndex,
	addCompletedCourse,
	setTotalCoursesToAdd,
	resetMultiCourseFlow,
	addCourseToAddedList,
	removeCourseFromAddedList,
	clearAddedCourses,
	setTechnicalCoursesCount,
	setVocationalCoursesCount,
} = courseSlice.actions;

export default courseSlice.reducer;
